/**
 * Types for the generic Column Options Editor dialog component.
 *
 * These types are intentionally decoupled from any specific domain (Assessment & Plan, hx-table, conditional-table,
 * etc.) so the editor can be reused across any column-based component.
 */

// ============================================================================
// Column Definition
// ============================================================================

/** Input type that the consuming component uses to render this column */
export type ColumnInputType = 'multi-select' | 'radio' | 'single-select' | 'text-input' | 'checkbox';

/** A single selectable option for a column */
export interface ColumnOption {
  value: string;
  label: string;
}

/**
 * Describes one configurable column that will appear as a tab in the editor. Pass an array of these as part of
 * {@link ColumnOptionsEditorConfig}.
 */
export interface ColumnOptionsEditorColumnDef {
  /** Unique string key identifying this column (used in rule conditions and actions) */
  key: string;
  /** Human-readable label shown in the tab header and rule dropdowns */
  label: string;
  /** How the consuming component renders this column's value */
  inputType: ColumnInputType;
  /** Optional hint text shown inside the tab to explain the column's purpose */
  hint?: string;
  /**
   * Optional special value that triggers extra behaviour in the consuming component (e.g. `'other'` enables a free-text
   * field, `'specialist-followup'` shows a specialist input). When set, the tab hint will mention this value.
   */
  specialValue?: string;
  /** Current list of selectable options for this column */
  options: ColumnOption[];
}

// ============================================================================
// Rule System
// ============================================================================

/** Comparison operators for rule conditions */
export type RuleOperator = 'equals' | 'not-equals' | 'includes' | 'excludes';

/** Action types available for rule effects */
export type RuleActionType = 'hide' | 'require' | 'disable';

/** "When" clause of a unified rule */
export interface RuleCondition {
  /** Key of the column whose current value is evaluated */
  column: string;
  operator: RuleOperator;
  values: string[];
}

/** "Then" clause of a unified rule */
export interface RuleAction {
  type: RuleActionType;
  /** Key of the column that the action targets */
  targetColumn?: string;
  /** Specific option values to hide or require */
  targetValues?: string[];
}

/** A single combined condition + action rule */
export interface UnifiedRule {
  id: string;
  when: RuleCondition;
  then: RuleAction;
  /** Whether this rule is currently active */
  enabled: boolean;
}

// ============================================================================
// Dialog Config
// ============================================================================

/**
 * The data object passed to {@link HxColumnOptionsEditorDialogComponent} via DIALOG_DATA. Provides the initial state for
 * all column tabs and the rules list.
 */
export interface ColumnOptionsEditorConfig {
  /** Column definitions — one tab is rendered per entry */
  columns: ColumnOptionsEditorColumnDef[];
  /** Pre-populated rules (editable within the modal) */
  rules: UnifiedRule[];
  /**
   * Optional default columns to restore when "Reset to Defaults" is clicked. If omitted, Reset to Defaults will restore
   * the columns passed in the `columns` property.
   */
  defaultColumns?: ColumnOptionsEditorColumnDef[];
  /**
   * Optional set of rules to restore when "Reset to Defaults" is clicked. If omitted, Reset to Defaults clears all
   * rules and restores original column options.
   */
  defaultRules?: UnifiedRule[];
}

// ============================================================================
// Operator Metadata
// ============================================================================

export interface OperatorMetadata {
  value: RuleOperator;
  label: string;
  supportsMultipleValues: boolean;
}

/** All operators available in the rules editor */
export const COLUMN_OPTIONS_EDITOR_OPERATORS: OperatorMetadata[] = [
  { value: 'equals', label: 'Equals', supportsMultipleValues: false },
  { value: 'not-equals', label: 'Not Equals', supportsMultipleValues: false },
  { value: 'includes', label: 'Includes', supportsMultipleValues: true },
  { value: 'excludes', label: 'Excludes', supportsMultipleValues: true },
];

// ============================================================================
// Validation
// ============================================================================

export interface RuleValidationResult {
  valid: boolean;
  errors: string[];
  duplicates: UnifiedRule[];
  conflicts: UnifiedRule[];
}

/**
 * Validates a set of unified rules for duplicates and logical conflicts.
 *
 * @param rules - Array of rules to validate
 * @returns Validation result with error messages, duplicate rules, and conflicting rules
 */
export function validateColumnOptionsRules(rules: UnifiedRule[]): RuleValidationResult {
  const errors: string[] = [];
  const duplicates: UnifiedRule[] = [];
  const conflicts: UnifiedRule[] = [];

  const ruleKey = (rule: UnifiedRule): string => {
    const whenKey = `${rule.when.column}:${rule.when.operator}:${[...rule.when.values].sort().join(',')}`;
    const thenKey = `${rule.then.type}:${rule.then.targetColumn ?? ''}:${[...(rule.then.targetValues ?? [])].sort().join(',')}`;
    return `${whenKey}|${thenKey}`;
  };

  const conditionKey = (rule: UnifiedRule): string =>
    `${rule.when.column}:${rule.when.operator}:${[...rule.when.values].sort().join(',')}`;

  // Detect duplicates (identical when + then)
  const rulesByKey = new Map<string, UnifiedRule[]>();
  rules.forEach((rule) => {
    const key = ruleKey(rule);
    if (!rulesByKey.has(key)) rulesByKey.set(key, []);
    rulesByKey.get(key)!.push(rule);
  });

  rulesByKey.forEach((group) => {
    if (group.length > 1) {
      duplicates.push(...group);
      errors.push(`Duplicate rules found: ${group.length} rules have identical conditions and actions.`);
    }
  });

  // Detect conflicts (same condition, contradictory actions on same target column)
  const rulesByCondition = new Map<string, UnifiedRule[]>();
  rules.forEach((rule) => {
    const key = conditionKey(rule);
    if (!rulesByCondition.has(key)) rulesByCondition.set(key, []);
    rulesByCondition.get(key)!.push(rule);
  });

  rulesByCondition.forEach((group) => {
    if (group.length <= 1) return;

    const hasConflict = group.some((rule1, i) =>
      group.slice(i + 1).some((rule2) => {
        if (rule1.then.targetColumn !== rule2.then.targetColumn) return false;
        if (rule1.then.type !== rule2.then.type) return true;

        if (
          (rule1.then.type === 'hide' && rule2.then.type === 'require') ||
          (rule1.then.type === 'require' && rule2.then.type === 'hide')
        ) {
          const values1 = new Set(rule1.then.targetValues ?? []);
          const values2 = new Set(rule2.then.targetValues ?? []);
          return [...values1].some((v) => values2.has(v));
        }

        return false;
      })
    );

    if (hasConflict) {
      conflicts.push(...group);
      errors.push(
        'Conflicting rules found: Multiple rules with the same condition have contradictory actions (e.g., hiding and requiring the same value).'
      );
    }
  });

  return { valid: errors.length === 0, errors, duplicates, conflicts };
}
